import './wallet-management.css'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useDragScroll from '../../hooks/useDragScroll'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { PaymentChannel } from '../../types/wallet-management'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import { numberWithCommas } from '../../utils/number'
import Icon from '../icon'
import { TailSpin } from '../loaders'
import DepositForm from './deposit-form'

type DepositProps = {
  showWalletManagementModal: (page?: number, tab?: number) => void
}

const Deposit = ({ showWalletManagementModal }: DepositProps) => {
  const { t } = useTranslation('walletManagement')
  const theme = useBoundStore(state => state.common.theme)
  const selectedPaymentChannel = useBoundStore(
    state => state.payment.selectedPaymentChannel,
  )
  const setSelectedPaymentChannel = useBoundStore(
    state => state.payment.setSelectedPaymentChannel,
  )
  const [isDepositChannelsLoading, setIsDepositChannelsLoading] =
    useState(false)
  const [paymentChannels, setPaymentChannels] = useState<PaymentChannel[]>([])

  const {
    scrollRef: paymentScrollRef,
    canScrollLeft,
    canScrollRight,
    hasDragged,
    onMouseDown,
    onMouseMove,
    onMouseUp,
  } = useDragScroll([paymentChannels])

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    setIsDepositChannelsLoading(true)
    api
      .post<ApiResponse<PaymentChannel[]>>(
        '/payment.php',
        {
          type: 'onlinepay_list_v2',
          payType: 0,
        },
        { signal },
      )
      .then(res => {
        const { status, info } = res.data
        if (status === 1) {
          setSelectedPaymentChannel(info[0])
          setPaymentChannels(info)
        }
        setIsDepositChannelsLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [])

  useEffect(() => {
    !selectedPaymentChannel && setSelectedPaymentChannel(paymentChannels[0])
  }, [selectedPaymentChannel])

  return (
    <div className="max-h-[calc(100dvh_-_49px)] xl:max-h-[calc(100dvh_-_54px)] overflow-y-auto xl:rounded-[10px]">
      {/* Payment Methods */}
      <div className="px-3 xl:px-6">
        <div className="deposit-panel rounded-lg bg-secondary p-3 mb-3 xl:p-4 xl:mb-4 dark:rounded-xl dark:border dark:border-solid dark:border-[rgba(255,255,255,0.03)] overflow-hidden">
          <p className="deposit-section-title font-semibold uppercase text-sm text-txt inline-block mb-2 xl:text-base xl:mb-3 dark:text-[#f6d56c] dark:font-bold relative">
            {t('payment-methods')}
          </p>
          <div className="relative">
            {canScrollLeft && (
              <div className="absolute pb-3 h-full w-[70px] pointer-events-none -left-8 top-0 flx-ctr pr-[30px] animate-arrowAnim z-[1]">
                <Icon
                  id="chevron-left"
                  width="7"
                  height="14"
                  fill="var(--secondary-txt)"
                />
              </div>
            )}
            {canScrollRight && (
              <div className="absolute pb-3 h-full w-[70px] pointer-events-none -right-7 top-0 flx-ctr pl-[30px] animate-arrowAnim z-[1]">
                <Icon
                  id="chevron-right"
                  width="7"
                  height="14"
                  fill="var(--secondary-txt)"
                />
              </div>
            )}
            <div
              ref={paymentScrollRef}
              className="payment-methods-container flex gap-2 overflow-x-auto pt-2 pb-2 -mt-2 xl:gap-3 cursor-grab select-none"
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}>
              {isDepositChannelsLoading && (
                <div className="w-full flx-ctr py-4">
                  <TailSpin width={40} height={40} color="#888" />
                </div>
              )}
              {!isDepositChannelsLoading &&
                paymentChannels.map(channel => (
                  <button
                    key={channel.id}
                    onClick={() => {
                      if (hasDragged.current) return
                      showWalletManagementModal(0, 0)
                      setSelectedPaymentChannel(channel)
                    }}
                    className={classNames(
                      'relative rounded-lg border-2 border-solid px-1 py-2 flex flex-col items-center gap-1 transition-all shrink-0 min-w-[40%] xl:py-3 xl:gap-2 dark:bg-[#1a1a1a] dark:border dark:border-solid dark:border-[rgba(255,255,255,0.08)] dark:rounded-lg',
                      selectedPaymentChannel?.id === channel.id
                        ? 'border-highlight deposit-payment-active dark:!border-[#f5c542] dark:text-[#f6e0a0]'
                        : 'border-secondary-txt-20 bg-secondary',
                    )}
                    style={
                      selectedPaymentChannel?.id === channel.id &&
                      theme === 'light'
                        ? {
                            background:
                              'linear-gradient(to bottom, color-mix(in srgb, var(--highlight) 20%, transparent), transparent)',
                          }
                        : undefined
                    }>
                    <img
                      className="max-w-[70px] h-10 xl:max-w-[100px] object-contain"
                      src={channel.img}
                      alt={channel.show_name}
                    />
                    <span className="text-xs xl:text-sm text-secondary-txt text-center leading-tight">
                      {[
                        channel.pay_limit.minimum
                          ? numberWithCommas(channel.pay_limit.minimum)
                          : null,
                        channel.pay_limit.maximum
                          ? numberWithCommas(channel.pay_limit.maximum)
                          : null,
                      ]
                        .filter(Boolean)
                        .join('-')}
                    </span>
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Amount Form */}
      <div className="px-3 xl:px-6 pb-6 xl:pb-12">
        <DepositForm />
      </div>
    </div>
  )
}

export default Deposit
